import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Info, Plus, Search, Send, Loader2 } from 'lucide-react';
import { LoadingBlock } from '@/components/ui/spinner';
import { parseServerTime } from '@/lib/serverTime';
import {
  marketingSendLogApi,
  marketingTargetSiteApi,
  MarketingEmailSendLog,
  MarketingManualCandidateResult,
  MarketingManualCandidateStatus,
  MarketingSendResult,
} from '@/api/marketingOutreachApi';
import { batchApi } from '@/api/batchApi';
import { BatchExecutionHistory, BatchExecutionStatus, BatchJobType } from '@/types/batch';

// ── 배치 수동 실행 + 실행 결과 (2026-09-23 신설) ────────────────────────────
// 발송 대상이 PENDING 큐에 있어도, 지금까지는 평일 오전 10시 자동 배치를 기다리거나
// 별도의 "배치 관리" 화면에서 이 잡을 찾아야만 발송을 트리거할 수 있었다. 이 화면에서
// 바로 발송을 실행하고, 그 배치의 처리 건수(0건이어도)까지 바로 볼 수 있게 한다.
const MARKETING_JOB_TYPE: BatchJobType = 'MARKETING_OUTREACH_DAILY';

// 이 API 도메인 앞의 CloudFront가 오리진 응답을 기본 30~60초까지만 기다리는데, 이
// 배치는 Anthropic 웹검색+SMTP 발송까지 포함해 그보다 오래 걸릴 수 있다(2026-09-24
// 확인, 78초 걸린 사례). BE가 대기시간(marketingSyncWaitSeconds) 안에 못 끝내면
// 백그라운드로 넘기고 즉시 응답하도록 바뀌었으므로, 여기서는 최근 실행이 아직
// RUNNING이면 몇 초마다 다시 조회(폴링)해서 완료 여부를 보여준다.
const BATCH_HISTORY_POLL_INTERVAL_MS = 5000;

const BATCH_STATUS_LABELS: Record<BatchExecutionStatus, string> = {
  RUNNING: '실행 중',
  SUCCESS: '성공',
  FAILED: '실패',
  PARTIAL_SUCCESS: '부분 성공',
  NEVER_EXECUTED: '미실행',
};

const BATCH_STATUS_COLORS: Record<BatchExecutionStatus, string> = {
  RUNNING: 'bg-blue-100 text-blue-700 border-blue-200',
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
  PARTIAL_SUCCESS: 'bg-orange-100 text-orange-700 border-orange-200',
  NEVER_EXECUTED: 'bg-gray-100 text-gray-600 border-gray-200',
};

// ── 마케팅 아웃리치(홍보 이메일) 발송 현황 (2026-09-13 신설) ────────────────
// 일자/사이트구분/사이트/이메일/발송결과로 필터링 가능한 발송 이력 조회 화면.

const RESULT_LABELS: Record<MarketingSendResult, string> = {
  SUCCESS: '성공',
  FAILED: '실패',
};

const RESULT_COLORS: Record<MarketingSendResult, string> = {
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
};

// ── 후보 사이트 수동 등록 (무상 경로, 2026-09-14 신설) ──────────────────────
// Anthropic 유료 웹검색 대신, 관리자가 후보 홈페이지 URL을 직접 붙여넣으면
// 서버가 접속해 회사명/이메일만 추출해 발송 대상으로 등록한다.

const CANDIDATE_CATEGORIES = [
  '온라인 쇼핑몰(자사몰/브랜드몰)',
  '국가기관/공공기관',
  '웹접근성 컨설팅/인증기관',
  '쇼핑몰 구축 대행사/에이전시',
  'SEO/마케팅 에이전시',
  '웹 유지보수/운영대행사',
  'UI/UX 디자인 스튜디오',
];

const CANDIDATE_STATUS_LABELS: Record<MarketingManualCandidateStatus, string> = {
  PENDING: '등록됨(발송대기)',
  EXCLUDED: '제외됨',
  DUPLICATE: '중복',
  ERROR: '오류',
};

const CANDIDATE_STATUS_COLORS: Record<MarketingManualCandidateStatus, string> = {
  PENDING: 'bg-green-100 text-green-700 border-green-200',
  EXCLUDED: 'bg-gray-100 text-gray-600 border-gray-200',
  DUPLICATE: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  ERROR: 'bg-red-100 text-red-700 border-red-200',
};

export default function AdminMarketingSendLogs() {
  const [logs, setLogs] = useState<MarketingEmailSendLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [category, setCategory] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<'' | MarketingSendResult>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [candidateUrls, setCandidateUrls] = useState('');
  const [candidateCategory, setCandidateCategory] = useState(CANDIDATE_CATEGORIES[0]);
  const [addingCandidates, setAddingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [candidateResults, setCandidateResults] = useState<MarketingManualCandidateResult[] | null>(null);

  const [batchHistory, setBatchHistory] = useState<BatchExecutionHistory[]>([]);
  const [batchHistoryLoading, setBatchHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendInfo, setSendInfo] = useState<string | null>(null);

  const fetchBatchHistory = useCallback(async () => {
    setBatchHistoryLoading(true);
    try {
      const res = await batchApi.getBatchHistory(MARKETING_JOB_TYPE, 0, 5);
      setBatchHistory(res.data.success ? res.data.data : []);
    } catch {
      // 배치 결과 조회 실패는 발송 이력 화면 전체를 막을 정도는 아니므로 조용히 무시
      setBatchHistory([]);
    } finally {
      setBatchHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatchHistory(); }, [fetchBatchHistory]);

  // 가장 최근 실행이 아직 RUNNING이면(백그라운드에서 계속 진행 중) 몇 초마다 다시
  // 조회해서, 완료되는 순간 화면이 자동으로 갱신되게 한다.
  const isLatestRunning = batchHistory[0]?.status === 'RUNNING';
  useEffect(() => {
    if (!isLatestRunning) return;
    const timer = setInterval(() => { fetchBatchHistory(); }, BATCH_HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isLatestRunning, fetchBatchHistory]);

  // 백그라운드 실행이 폴링 중 완료되면 "실행 중" 안내 배너를 자동으로 치운다.
  useEffect(() => {
    if (!isLatestRunning) setSendInfo((prev) => (prev ? null : prev));
  }, [isLatestRunning]);

  const handleSendNow = async () => {
    setShowSendConfirm(false);
    setSending(true);
    setSendError(null);
    setSendInfo(null);
    try {
      const res = await batchApi.executeManually(MARKETING_JOB_TYPE);
      if (!res.data.success) {
        setSendError(res.data.message || '발송 실행에 실패했습니다.');
      } else if (res.data.completedSynchronously === false) {
        setSendInfo(res.data.message || '백그라운드에서 실행 중입니다. 잠시 후 결과가 자동으로 갱신됩니다.');
      }
      // 실패했더라도(예: 중복 실행 방지) 최신 상태는 다시 보여준다
      await Promise.all([fetchBatchHistory(), fetchLogs()]);
    } catch (err: any) {
      setSendError(err?.response?.data?.message ?? '발송 실행 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingSendLogApi.search({
        page,
        size,
        category: category.trim() || undefined,
        companyName: companyName.trim() || undefined,
        email: email.trim() || undefined,
        result: result || undefined,
        startDate: startDate ? `${startDate}T00:00:00` : undefined,
        endDate: endDate ? `${endDate}T23:59:59` : undefined,
      });
      const data = res.data;
      setLogs(data.content ?? []);
      setTotalElements(data.totalElements ?? 0);
      setTotalPages(data.totalPages ?? 0);
    } catch {
      setError('발송 현황을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    // page가 이미 0이면 useEffect가 재실행 안 되므로 직접 호출
    if (page === 0) fetchLogs();
  };

  const handleReset = () => {
    setCategory('');
    setCompanyName('');
    setEmail('');
    setResult('');
    setStartDate('');
    setEndDate('');
    setPage(0);
    setTimeout(fetchLogs, 0);
  };

  const handleAddCandidates = async () => {
    const urls = candidateUrls
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (urls.length === 0) {
      setCandidateError('홈페이지 URL을 1개 이상 입력해주세요.');
      return;
    }
    setAddingCandidates(true);
    setCandidateError(null);
    try {
      const res = await marketingTargetSiteApi.manualAdd({ urls, category: candidateCategory });
      setCandidateResults(res.data);
      setCandidateUrls('');
    } catch (err: any) {
      setCandidateError(err?.response?.data?.message ?? '후보 등록에 실패했습니다.');
    } finally {
      setAddingCandidates(false);
    }
  };

  const formatDateTime = (iso: string) => {
    const date = parseServerTime(iso);
    return date
      ? date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '-';
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="마케팅 아웃리치 발송 현황"
        description="홍보 이메일 발송 이력을 일자/사이트구분/사이트/이메일/발송결과로 조회합니다."
        action={
          // isLatestRunning: handleSendNow의 setSending(false)는 BE가 CloudFront
          // 대기시간 안에 응답을 돌려주는 즉시(백그라운드로 넘어간 경우 포함) 실행되므로,
          // 실제 배치가 서버에서 몇 분씩 더 실행 중이어도 sending만으로는 금방 false가
          // 되어 버튼이 다시 눌려버렸다(2026-09-30 사용자 확인) - 배치 실행 이력이
          // RUNNING인 동안에도 함께 비활성화한다.
          <Button onClick={() => setShowSendConfirm(true)} disabled={sending || isLatestRunning}>
            {sending || isLatestRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? '발송 실행 중...' : isLatestRunning ? '이미 실행 중...' : '지금 발송 실행'}
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {sendError && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{sendError}</AlertDescription>
        </Alert>
      )}

      {sendInfo && (
        <Alert className="border-blue-300 bg-blue-50 text-blue-900">
          <Info className="w-4 h-4" />
          <AlertDescription>{sendInfo}</AlertDescription>
        </Alert>
      )}

      {showSendConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSendConfirm(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">마케팅 아웃리치 발송 실행</h3>
              <p className="text-gray-700 mb-4">
                지금 바로 홍보 이메일 발송 배치를 실행하시겠습니까? <strong>실제 수신자에게 메일이 발송됩니다.</strong>
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                  <div className="text-sm text-yellow-800">
                    <div className="font-medium mb-1">주의사항</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>오늘 이미 발송한 건수를 빼고 남은 한도만큼만 발송합니다</li>
                      <li>PENDING 대상이 부족하면 Anthropic 웹검색으로 신규 후보를 자동 발굴합니다(비용 발생)</li>
                      <li>이미 실행 중인 동일 배치가 있다면 중복 실행이 방지됩니다</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowSendConfirm(false)}>취소</Button>
                <Button onClick={handleSendNow}>확인 및 실행</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>배치 실행 결과</CardTitle>
          <CardDescription>
            최근 5건 - 발송 대상이 0건이었던 실행도 포함해 보여줍니다. "실행 중"인 동안은 자동으로 갱신됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {batchHistoryLoading ? (
            <LoadingBlock size="md" py={8} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>실행일시</TableHead>
                    <TableHead>구분</TableHead>
                    <TableHead className="text-center">상태</TableHead>
                    <TableHead className="text-center">총건수</TableHead>
                    <TableHead className="text-center">성공</TableHead>
                    <TableHead className="text-center">실패</TableHead>
                    <TableHead>결과 메시지</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-400 py-10 text-sm">
                        아직 실행된 적이 없습니다. "지금 발송 실행" 버튼으로 바로 실행해보세요.
                      </TableCell>
                    </TableRow>
                  ) : batchHistory.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-xs whitespace-nowrap text-gray-500">
                        {formatDateTime(h.startedAt)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {h.isManualExecution ? '수동' : '자동'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${BATCH_STATUS_COLORS[h.status]}`}>
                          {BATCH_STATUS_LABELS[h.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{h.totalCount}</TableCell>
                      <TableCell className="text-center text-sm text-green-700">{h.successCount}</TableCell>
                      <TableCell className="text-center text-sm text-red-700">{h.failedCount}</TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-md">
                        {h.resultMessage ?? h.errorMessage ?? '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>후보 사이트 수동 등록</CardTitle>
          <CardDescription>
            발송 대상으로 삼고 싶은 홈페이지 URL을 한 줄에 하나씩 붙여넣으면, 서버가 접속해 회사명과
            이메일(제휴/협찬/유통 문의, 개인정보보호책임자 등)을 자동으로 추출해 발송 대상으로 등록합니다.
            이메일을 찾지 못하면 발송 대상에서 제외됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {candidateError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{candidateError}</AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-2 block">홈페이지 URL (한 줄에 하나씩)</label>
              <textarea
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                rows={5}
                placeholder={'https://example-shop.com\nhttps://another-site.co.kr'}
                value={candidateUrls}
                onChange={(e) => setCandidateUrls(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">사이트구분(카테고리)</label>
              <select
                value={candidateCategory}
                onChange={(e) => setCandidateCategory(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {CANDIDATE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <Button onClick={handleAddCandidates} disabled={addingCandidates} className="w-full mt-3">
                <Plus className="w-4 h-4 mr-2" />
                {addingCandidates ? '등록 중...' : '후보 등록'}
              </Button>
            </div>
          </div>

          {candidateResults && (
            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>회사명</TableHead>
                    <TableHead className="text-center">결과</TableHead>
                    <TableHead>메시지</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidateResults.map((r, idx) => (
                    <TableRow key={`${r.url}-${idx}`}>
                      <TableCell className="text-xs text-gray-600 max-w-xs truncate">{r.url}</TableCell>
                      <TableCell className="text-sm">{r.companyName ?? '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${CANDIDATE_STATUS_COLORS[r.status]}`}>
                          {CANDIDATE_STATUS_LABELS[r.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>검색 필터</CardTitle>
          <CardDescription>사이트구분(카테고리), 사이트명, 이메일, 발송결과, 기간으로 필터링</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Input
              placeholder="사이트구분(카테고리)"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Input
              placeholder="사이트(회사명)"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Input
              placeholder="이메일"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <select
              value={result}
              onChange={(e) => setResult(e.target.value as '' | MarketingSendResult)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">전체 결과</option>
              <option value="SUCCESS">성공</option>
              <option value="FAILED">실패</option>
            </select>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              검색
            </Button>
            <Button onClick={handleReset} variant="outline" disabled={loading}>
              초기화
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>발송 이력</CardTitle>
          <CardDescription>전체 {totalElements.toLocaleString()}건</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>발송일자</TableHead>
                    <TableHead>사이트구분</TableHead>
                    <TableHead>사이트</TableHead>
                    <TableHead>이메일</TableHead>
                    <TableHead className="text-center">발송결과</TableHead>
                    <TableHead>발송사유 / 실패사유</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-16 text-sm">
                        조회된 발송 이력이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : logs.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs whitespace-nowrap text-gray-500">
                        {formatDateTime(entry.sentAt)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{entry.categorySnapshot ?? '-'}</TableCell>
                      <TableCell className="text-sm font-medium">{entry.companyNameSnapshot}</TableCell>
                      <TableCell className="text-xs text-gray-600">{entry.toEmail}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${RESULT_COLORS[entry.result]}`}>
                          {RESULT_LABELS[entry.result]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-md">
                        {entry.result === 'FAILED' ? (entry.failReason ?? '-') : (entry.aiReason ?? '-')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                variant="outline"
              >
                이전
              </Button>
              <span className="px-4 py-2 text-sm">
                {page + 1} / {totalPages}
              </span>
              <Button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                variant="outline"
              >
                다음
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
